import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import FAQAccordion from '../Services/FAQAccordion';
import Seo from '../Seo';

const SolutionDirect = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);



  const faqs = [
    {
      q: "What is Direct Sourcing?",
      a: "Direct Sourcing is a workforce strategy where organizations use their own employer brand to attract and engage contingent talent directly. Instead of relying solely on third-party staffing channels, this approach builds a reusable private talent ecosystem for recurring demand. It improves control over candidate quality, experience, and engagement velocity across critical roles. With structured governance, direct sourcing also enhances visibility across cost, supplier dependency, and fulfillment performance. Over time, it becomes a strategic capability for faster and more efficient talent acquisition."
    },
    {
      q: "How does it save money?",
      a: "Direct sourcing reduces cost primarily by lowering dependency on high-markup intermediary channels for recurring contingent hiring. As your private talent community matures, fulfillment speed and quality improve while acquisition overhead becomes more predictable. Organizations also reduce process duplication by standardizing intake, screening, and engagement workflows in one governed model. This lowers operational friction across procurement, HR, and business teams involved in workforce decisions. The result is a stronger cost-to-value ratio without sacrificing access to qualified talent."
    },
    {
      q: "Does this integrate with my VMS?",
      a: "Yes. Our direct sourcing delivery model is designed to integrate with major Vendor Management Systems and existing governance workflows. We align requisition intake, candidate tracking, and engagement controls so teams can operate in familiar systems without process disruption. Integration planning also includes approval flows, reporting structures, and compliance checkpoints based on your operating model. This ensures your direct talent channel works as an extension of your current workforce infrastructure. The objective is seamless adoption with measurable improvement in fulfillment speed and control."
    },
    {
      q: "How long does it take to build a private talent community?",
      a: "Timeline depends on role volume, brand maturity, and current contingent demand patterns, but rollout can be phased for early value. We typically begin with high-frequency role categories to validate process and demonstrate measurable gains quickly. Community growth is supported through targeted outreach, re-engagement flows, and candidate experience optimization. As performance stabilizes, the model is expanded to additional skill tracks and business units. This phased method reduces risk while building long-term sourcing strength."
    },
    {
      q: "Can direct sourcing improve candidate quality, not just speed?",
      a: "Yes, direct sourcing improves quality by enabling repeated engagement with known talent pools instead of one-time transactional sourcing. Over time, organizations gain better fit insight through historical performance, role outcomes, and engagement feedback data. This helps prioritize candidates with stronger delivery consistency and organizational alignment. Structured governance and scoring frameworks further improve shortlist precision for hiring managers. The result is better quality decisions with higher predictability across recurring contingent hiring."
    }
  ];

  return (
    <div className="bg-white min-h-screen pt-4 [&_.text-h1]:text-h2 [&_.text-h2]:text-h3 [&_.text-body]:text-small [&_.text-small]:text-subtext">
      <Seo
        title="Direct Sourcing"
        description="Libsys direct sourcing — access top-tier IT talent directly, eliminate intermediaries, and reduce time-to-hire by up to 60% with a private talent community."
        path="/solutions/direct"
      />
      {/* ══ HERO SECTION ══════════════════════════════════════════════════════ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-6">
        <div className="rounded-[2.5rem] border border-[#236086]/10 bg-slate-50/70 p-6 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="lg:col-span-5">
              <span className="inline-flex items-center gap-2 text-red-tagline mb-5">
                Modern Direct Sourcing Strategy
              </span>
              <h1 className="text-h1 text-primary mb-6">
                Your Brand, Our Expertise. The <span className="text-secondary">Shortest Path</span> to Top Talent
              </h1>
              <p className="text-slate-600 text-body leading-[1.8] font-medium mb-8 max-w-2xl">
                The traditional reliance on third-party staffing agencies is increasingly becoming a strategic bottleneck for high-velocity enterprises. Libsys Direct Sourcing empowers your organization to build, own, and leverage its own private talent communities. By integrating sophisticated curation technology with your existing brand equity, we help you reduce dependency on external supplier markups while significantly improving the quality and cultural alignment of your contingent workforce. Our model ensures that you maintain full control over the candidate experience while driving down the total cost of ownership for specialized talent.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8 mb-10">
                {[
                  'Private branded talent spheres',
                  'AI-powered shortlist precision',
                  'VMS-ready operational sync',
                  'Reduced supplier dependency',
                  'Predictive re-engagement flows',
                  'Enterprise-grade quality vetting'
                ].map((item) => (
                  <li key={item} className="text-slate-700 text-small font-bold leading-relaxed flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-secondary mt-[7px] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-4">
                <a
                  href="tel:+16307991556"
                  className="inline-flex items-center gap-3 px-10 py-5 bg-primary hover:bg-secondary text-white font-black text-[11px] uppercase tracking-widest rounded-xl transition-all shadow-xl active:scale-95 group"
                >
                  Schedule Strategy Review
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="lg:col-span-7 lg:-mt-12">
              <div className="rounded-[2.5rem] border border-slate-200 shadow-[0_30px_70px_-15px_rgba(35,96,134,0.2)] bg-slate-100 relative group overflow-hidden">
                <div className="absolute inset-0 bg-primary/5 group-hover:bg-transparent transition-colors z-10 pointer-events-none" />
                 <img
                    src="/award-logos/Directsourcing1.png"
                    alt="Direct Sourcing simulation"
                    className="w-full h-[400px] lg:h-[600px] object-cover group-hover:scale-105 transition-transform duration-1000"
                    onError={(e) => { e.currentTarget.src = '/hero_tech_office.png'; }}
                  />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ STRATEGIC BENEFITS SECTION ═══════════════════════════════════════ */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-h2 text-primary mb-6">Build a Sustainable Talent Asset</h2>
            <p className="text-slate-600 font-medium leading-relaxed">
              We transition your hiring from a reactive, transactional model to a strategic, brand-driven capability that creates long-term value.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: "Cost Preservation", desc: "Lower your average contingent spend by reducing dependency on third-party suppliers and their associated markups." },
              { title: "Brand Engagement", desc: "Turn your company into a talent magnet, engaging consultants who are specifically aligned with your brand values and culture." },
              { title: "Workflow Speed", desc: "Our VMS-integrated pipelines ensure that pre-vetted candidates are ready for interview within hours, not weeks." }
            ].map((benefit, i) => (
              <div key={i} className="p-10 rounded-[2rem] bg-[#e2edf5] border border-[#236086]/10 hover:shadow-xl transition-all">
                <h4 className="text-xl font-black text-primary mb-4">{benefit.title}</h4>
                <p className="text-slate-600 text-body leading-relaxed font-medium">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CORE VALUE ══════════════════════════════════════════════════════ */}
      <section className="bg-slate-50 border-y border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="text-red-tagline block mb-4">Channel Optimization</span>
              <h2 className="text-h2 text-primary mb-6">Controlled Communities, Proven Quality</h2>
              <p className="text-slate-600 text-small mb-8 leading-[1.8] font-medium">
                Our direct sourcing methodology is built to transform your employer brand into a primary talent channel. We helps you move away from transactional, high-cost hiring by building a reusable branded community that matures with every interaction. By integrating our curation expertise with your existing enterprise systems (VMS/ATS), we create a seamless experience for both hiring managers and candidates. Libsys provides the governance and intelligent shortlisting needed to ensure that 'direct' always means 'highest quality' for your specialized contingency needs.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8 mb-10">
                {[
                  { title: "Owned Talent", desc: "Private talent communities fully aligned with your brand identity." },
                  { title: "Governed Curation", desc: "Intelligent role-fit matching to reduce hiring manager review time." },
                  { title: "Stack Integration", desc: "Frictionless workflow sync with your enterprise VMS and HRIS." },
                  { title: "Spend Control", desc: "Transparent markup management and supplier spend reduction." }
                ].map((item, i) => (
                  <div key={i} className="space-y-2.5">
                    <div className="text-secondary flex items-center gap-2">
                      <span className="font-black text-primary text-tiny uppercase tracking-widest">{item.title}</span>
                    </div>
                    <p className="text-slate-600 text-subtext leading-relaxed font-semibold">{item.desc}</p>
                  </div>
                ))}
              </div>
              <ul className="space-y-4">
                {[
                  'Customized branded portals to enhance candidate engagement',
                  'Dedicated curation teams assigned to specific skill categories',
                  'Data-driven re-engagement plans to maximize talent community ROI'
                ].map((item) => (
                  <li key={item} className="text-slate-800 text-subtext font-bold leading-relaxed flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-secondary mt-2 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
            
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative -mt-4 lg:-mt-6">
               <div className="rounded-[2.5rem] overflow-hidden border border-[#236086]/10 shadow-2xl bg-white p-2">
                 <img
                  src="/award-logos/Directsourcing2.png"
                  alt="Direct sourcing operations"
                  className="w-full h-auto aspect-square lg:aspect-[4/3] object-cover rounded-[2.2rem]"
                  onError={(e) => { e.currentTarget.src = '/hero_tech_office.png'; }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ CTA SECTION ═══════════════════════════════════════════════════════ */}
      <section className="py-12 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="bg-primary rounded-[3rem] p-8 lg:py-10 lg:px-12 relative overflow-hidden text-center lg:text-left">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-secondary/10 rounded-full blur-2xl -ml-20 -mb-20 pointer-events-none" />
          
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <span className="text-blue-100/60 text-tiny font-black uppercase tracking-[0.4em] mb-2 block">Enterprise Growth</span>
              <h2 className="text-3xl lg:text-4xl font-black text-white mb-4 leading-tight">
                Secure High-Impact <span className="text-secondary">Direct Hires</span>
              </h2>
              <p className="text-blue-100/80 text-lg mb-6 max-w-2xl font-medium">
                Bridge specialized leadership and development gaps with governed, high-performance permanent talent. Maintain project momentum with absolute agility and uncompromised quality.
              </p>
              <div className="flex flex-wrap justify-center lg:justify-start gap-4">
                <a
                  href="tel:+16307991556"
                  className="px-10 py-5 bg-white text-primary font-black rounded-xl hover:bg-blue-50 transition-all flex items-center gap-3 uppercase tracking-widest text-subtext shadow-2xl active:scale-95"
                >
                  630-799-1556
                  <ArrowRight className="w-5 h-5" />
                </a>
              </div>
            </div>
            <div className="lg:col-span-4">
              <div className="grid grid-cols-1 gap-3">
                {[
                  { k: 'Precision', v: 'Role-Aligned Sourcing' },
                  { k: 'Longevity', v: 'Retention-Focused Vetting' },
                  { k: 'Impact', v: 'Accelerated Time-to-Value' }
                ].map((item) => (
                  <div key={item.k} className="bg-white/10 backdrop-blur-md border border-white/10 p-4 rounded-2xl text-left">
                    <p className="text-blue-100/50 text-[10px] font-black uppercase tracking-widest">{item.k}</p>
                    <p className="text-white text-small font-bold mt-1">{item.v}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <FAQAccordion faqs={faqs} />
    </div>
  );
};

export default SolutionDirect;
